import React from "react";
import { motion } from "framer-motion";
import {
  Hammer, Building, BrickWall, Layers, Paintbrush,
  Droplets, LayoutGrid, ShieldCheck, Triangle, Wrench, ArrowUpRight,
} from "lucide-react";
import { useLang } from "../i18n";
import { RevealText } from "./Reveal";

const ICONS = [Hammer, Building, BrickWall, Layers, Paintbrush, Droplets, LayoutGrid, ShieldCheck, Triangle, Wrench];

// Featured tiles get background images + larger spans (bento tension)
const FEATURED = {
  0: "https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts/8cwkyr75_WhatsApp%20Image%202026-08-05%20at%2013.36.35.webp",
  1: "https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts/2ok9r6wf_WhatsApp%20Image%202026-08-05%20at%2018.16.58%20%284%29.webp",
  3: "https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts/2wzhp54z_WhatsApp%20Image%202026-08-05%20at%2013.36.30.webp",
  7: "https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts/b2548xq0_afa90d76-58c0-4f8d-b205-89a27a874f39.jpg",
};
const SPANS = {
  0: "md:col-span-2 md:row-span-2",
  3: "md:col-span-2",
};

export default function Servicios() {
  const { t } = useLang();
  const s = t.servicios;

  return (
    <section id="servicios" className="relative py-24 md:py-32 bg-surface grain">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-brand-orange" />
          <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-brand-orange">{s.overline}</span>
        </div>
        <RevealText
          as="h2"
          text={s.title}
          className="font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl block max-w-3xl"
        />

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[minmax(180px,1fr)] gap-px bg-border border border-border">
          {s.items.map((item, i) => {
            const Icon = ICONS[i] || Wrench;
            const featured = FEATURED[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-6%" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-${i}`}
                className={`group relative overflow-hidden bg-bg p-6 md:p-7 flex flex-col justify-between ${SPANS[i] || ""}`}
              >
                {featured && (
                  <>
                    <img src={featured} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-[opacity,transform] duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/70 to-bg/30" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <Icon className="h-8 w-8 text-brand-orange" strokeWidth={1.5} />
                  <ArrowUpRight className="h-5 w-5 text-steel-500 opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-[opacity,transform] duration-300" />
                </div>
                <div className="relative">
                  <h3 className={`font-display font-semibold tracking-tight leading-tight ${featured ? "text-2xl md:text-3xl" : "text-xl"}`}>
                    {item.t}
                  </h3>
                  <p className="mt-2 text-sm text-steel-400 leading-relaxed max-w-xs">{item.d}</p>
                </div>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-brand-orange group-hover:w-full transition-[width] duration-500" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
